import { ExportFailure } from '../shared/export-failure.js';
import { isObject, isString } from '../shared/runtime-values.js';
import type Map from 'ol/Map.js';
import { transformExtent } from 'ol/proj.js';

export class SatelliteExportError extends ExportFailure {
  constructor(message: string, code: 'satellite_attribution_failed' | 'satellite_region_restricted' = 'satellite_attribution_failed') { super(code, message); }
}

export const AZURE_PRINT_NOTICE = 'Microsoft product screen shot(s) reprinted with permission from Microsoft Corporation.';
let currentCredits = '© Microsoft';
let attributionRoute = '/api/azure-satellite';
const mapRequests = new WeakMap<Map, number>();
const requests = new globalThis.Map<string, Promise<string[]>>();

export function configureAzureAttributionRoute(tileUrl: string): void {
  const url = new URL(tileUrl, 'https://local.invalid');
  for (const name of ['x', 'y', 'z', 'scale']) url.searchParams.delete(name);
  attributionRoute = url.pathname + url.search;
  requests.clear();
}

export function getAzureAttributionHtml(): string {
  return currentCredits.replaceAll('&', '&amp;').replaceAll('<', '&lt;').replaceAll('>', '&gt;').replaceAll('"', '&quot;');
}

function attributionBounds(bounds: number[]): number[][] {
  let [west, south, east, north] = bounds;
  if (east < west) east += 360;
  const span = east - west;
  south = Math.max(-85.051129, south); north = Math.min(85.051129, north);
  if (span >= 360) return [[-180, south, 180, north]];
  west = ((west + 180) % 360 + 360) % 360 - 180;
  east = west + span;
  return east > 180
    ? [[west, south, 180, north], [-180, south, east - 360, north]]
    : [[west, south, east, north]];
}

export async function loadAzureAttribution(bounds: number[], zoom: number, hybrid = false): Promise<string> {
  const regions = await Promise.all(attributionBounds(bounds).flatMap(region => [loadRegionAttribution(region, zoom), ...(hybrid ? [loadRegionAttribution(region, zoom, true)] : [])]));
  return ['© Microsoft', ...new Set(regions.flat())].join(' · ');
}

async function loadRegionAttribution(bounds: number[], zoom: number, hybrid = false): Promise<string[]> {
  const params = new URLSearchParams({ kind: 'attribution', z: String(Math.max(1, Math.min(19, Math.floor(zoom)))), bounds: bounds.join(',') });
  if (hybrid) params.set('layer', 'labels');
  const url = `${attributionRoute}${attributionRoute.includes('?') ? '&' : '?'}${params}`;
  let request = requests.get(url);
  if (!request) {
    request = fetch(url, { credentials: 'same-origin', signal: AbortSignal.timeout(15_000) }).then(async response => {
      if (!response.ok) throw new SatelliteExportError('Satellite credits are unavailable. Try again.');
      const payload: unknown = await response.json();
      if (!payload || !isObject(payload) || !('copyrights' in payload) || !Array.isArray(payload.copyrights) || !payload.copyrights.length || !payload.copyrights.every(value => isString(value))) throw new SatelliteExportError('Satellite credits are unavailable. Try again.');
      const credits = payload.copyrights.map(html => new DOMParser().parseFromString(html, 'text/html').body.textContent?.trim() ?? '').filter(Boolean);
      if (!credits.length) throw new SatelliteExportError('Satellite credits are unavailable. Try again.');
      return credits;
    });
    if (requests.size >= 32) requests.delete(requests.keys().next().value!);
    requests.set(url, request);
    void request.catch(() => { if (requests.get(url) === request) requests.delete(url); });
  }
  return request;
}

export function publishAzureAttribution(credits: string): void {
  currentCredits = credits;
  window.dispatchEvent(new Event('azure-attribution-changed'));
}

export async function updateAzureMapAttribution(map: Map): Promise<string> {
  const version = (mapRequests.get(map) ?? 0) + 1;
  mapRequests.set(map, version);
  const view = map.getView();
  const bounds = transformExtent(view.calculateExtent(map.getSize()), view.getProjection(), 'EPSG:4326');
  const credits = await loadAzureAttribution(bounds, view.getZoom() ?? 1, map.getLayers().item(0)?.get('basemapId') === 'hybrid');
  if (mapRequests.get(map) === version) publishAzureAttribution(credits);
  return credits;
}

export function azurePrintAttribution(credits: string): string {
  if (!credits.trim()) throw new SatelliteExportError('Satellite credits are unavailable. Try again.');
  if (/zenrin/iu.test(credits)) throw new SatelliteExportError('Satellite downloads are not available for this area. Select another map style.', 'satellite_region_restricted');
  return `${credits} · ${AZURE_PRINT_NOTICE}`;
}

export function satellitePreview(width: number, height: number): string {
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(`<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 800 500"><rect width="800" height="500" fill="#e8eef1"/><g fill="#334155" font-family="sans-serif" text-anchor="middle"><text x="400" y="230" font-size="24">Satellite imagery is included in your download.</text><text x="400" y="270" font-size="18">Use the live map to check your drawing.</text></g></svg>`)}`;
}
